import React, { useState, useEffect } from "react";
import { Wallet, Plus, FileDown, ClipboardCheck, Lock, Unlock, History } from "lucide-react";
import { STATUS_STYLES } from "./payrollHelpers";

export default function PayrollPeriodHeader({
  period, fromDate, toDate, canPayroll, busy,
  onTransition, onPatchPayDate, onAddAdjustment, onExport, onShowAudit,
}) {
  const status = period?.status || "OPEN";
  const style = STATUS_STYLES[status] || STATUS_STYLES.OPEN;
  const [payDate, setPayDate] = useState(period?.payDate || "");
  useEffect(() => { setPayDate(period?.payDate || ""); }, [period?.payDate]);

  const canEditMoney = canPayroll("edit_hours") || canPayroll("edit_commissions") || canPayroll("edit_bonuses") || canPayroll("add_adjustments");
  const confirmAndTransition = (toStatus, msg, extra) => {
    if (!window.confirm(msg)) return;
    onTransition(toStatus, extra);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="payroll-period-header">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[16px] font-bold text-slate-900">
            <Wallet className="h-4.5 w-4.5 text-blue-600" /> Payroll
            <span
              data-testid="payroll-status-pill"
              className={`inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${style.cls}`}
            >
              {status === "LOCKED" && <Lock className="h-3 w-3" />} {style.label}
            </span>
          </div>
          <div className="text-[13px] text-slate-600 mt-0.5">{fromDate} – {toDate}</div>
          <div className="flex items-center gap-1.5 mt-1 text-[12px] text-slate-500">
            Pay date:
            {status !== "OPEN" || !canEditMoney ? (
              <span className="font-medium text-slate-700">{period?.payDate || "—"}</span>
            ) : (
              <input
                type="date" value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
                onBlur={() => payDate !== (period?.payDate || "") && onPatchPayDate(payDate)}
                data-testid="payroll-pay-date-input"
                className="h-6 px-1.5 rounded border border-slate-200 text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status !== "LOCKED" && canEditMoney && (
            <button
              onClick={onAddAdjustment} data-testid="payroll-add-adjustment-btn"
              className="h-8 px-3 rounded-md border border-slate-200 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" /> Add Adjustment
            </button>
          )}
          {status === "OPEN" && canPayroll("review_exceptions") && (
            <button
              onClick={() => confirmAndTransition("REVIEW", "Move this payroll period to Review? This freezes the current system-calculated values as the adjustment baseline.", {})}
              disabled={busy} data-testid="payroll-move-review-btn"
              className="h-8 px-3 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
            >
              <ClipboardCheck className="h-3.5 w-3.5" /> Review Payroll
            </button>
          )}
          {status === "REVIEW" && (
            <>
              {canPayroll("review_exceptions") && (
                <button
                  onClick={() => confirmAndTransition("OPEN", "Move this payroll period back to Open for further edits?", {})}
                  disabled={busy} data-testid="payroll-back-to-open-btn"
                  className="h-8 px-3 rounded-md border border-slate-200 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Back to Open
                </button>
              )}
              {canPayroll("approve") && (
                <button
                  onClick={() => confirmAndTransition("APPROVED", "Approve this payroll period? The reviewed figures will be locked in for payout.", {})}
                  disabled={busy} data-testid="payroll-approve-btn"
                  className="h-8 px-3 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[12px] font-semibold disabled:opacity-50"
                >
                  Approve Payroll
                </button>
              )}
            </>
          )}
          {status === "APPROVED" && (
            <>
              {canPayroll("approve") && (
                <button
                  onClick={() => confirmAndTransition("REVIEW", "Send this payroll period back to Review?", {})}
                  disabled={busy} data-testid="payroll-back-to-review-btn"
                  className="h-8 px-3 rounded-md border border-slate-200 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Back to Review
                </button>
              )}
              {canPayroll("lock") && (
                <button
                  onClick={() => confirmAndTransition("LOCKED", "Lock this payroll period? All payroll data becomes read-only until reopened.", {})}
                  disabled={busy} data-testid="payroll-lock-btn"
                  className="h-8 px-3 rounded-md bg-slate-800 hover:bg-slate-900 text-white text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Lock className="h-3.5 w-3.5" /> Lock Period
                </button>
              )}
            </>
          )}
          {status === "LOCKED" && canPayroll("reopen") && (
            <button
              onClick={() => {
                const reason = window.prompt("Reason for reopening this locked payroll period (required):");
                if (reason && reason.trim()) onTransition("OPEN", { reason: reason.trim() });
              }}
              disabled={busy} data-testid="payroll-reopen-btn"
              className="h-8 px-3 rounded-md border border-rose-300 text-rose-700 text-[12px] font-semibold hover:bg-rose-50 flex items-center gap-1.5"
            >
              <Unlock className="h-3.5 w-3.5" /> Reopen Period
            </button>
          )}
          <button
            onClick={onShowAudit} data-testid="payroll-audit-history-btn"
            className="h-8 px-3 rounded-md border border-slate-200 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
          >
            <History className="h-3.5 w-3.5" /> Audit History
          </button>
          <button
            onClick={onExport} data-testid="payroll-export-btn"
            className="h-8 px-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] font-semibold hover:bg-emerald-100 flex items-center gap-1.5"
          >
            <FileDown className="h-3.5 w-3.5" /> Export
          </button>
        </div>
      </div>
    </div>
  );
}
